import {Model} from 'backbone';
import _ from 'underscore';

import GapSequenceWrapper from 'analyses/alignment/GapSequenceWrapper';
import {GAP} from 'biojs/BioConstants';
import * as BiojsTranslation from 'biojs/Translation';
import {AminoAcidMap, READING_FRAME_NAMES, ReadingFrameName} from 'constants/AlignmentConstants';
import {AminoAcid} from 'molbio/MolBioConstants';
import ComponentTranslation from 'molbio/translations/backbone/Translation';

import * as util from './Util';

type FrameStarts = {[k in ReadingFrameName]: number | null};

/**
 * Find the index of the start of each reading frame, skipping gaps
 * to ensure each reading frame starts on a real base.
 *
 * Not all frames may exist: in 'AA-------', there is no valid third reading frame because there are no
 * valid starting points (a real base) after the second index.
 */
export function getFrameStarts(bases: string, trimStart: number): FrameStarts {
  const fwd1 = util.nextNonGapIndex(bases, trimStart);
  const fwd2 = fwd1 > -1 ? util.nextNonGapIndex(bases, fwd1 + 1) : -1;
  const fwd3 = fwd2 > -1 ? util.nextNonGapIndex(bases, fwd2 + 1) : -1;
  return {
    FWD1: fwd1 > -1 ? fwd1 : null,
    FWD2: fwd2 > -1 ? fwd2 : null,
    FWD3: fwd3 > -1 ? fwd3 : null,
  };
}

/**
 * Get amino acids for a reading frame on a sequence in an alignment.
 *
 * @param readingFrameStart: index on the sequence to start translating from (affects the reading frame)
 * @param translationStart: index on the non-master sequence to start finding amino acids
 * @param end: index to end the translation (exclusive)
 */
export function getReadingFrameAminoAcids(
  gapSequenceWrapper: GapSequenceWrapper,
  readingFrameStart: number,
  translationStart: number,
  end: number
): Array<AminoAcid> {
  // have to make a ComponentTranslation because it has the getAminoAcids logic
  const translation = new ComponentTranslation({
    _translation: new BiojsTranslation.Model({
      start: readingFrameStart,
      end,
      strand: 1,
      fromSubPart: false,
    }),
  });
  return translation.getAminoAcids(gapSequenceWrapper, translationStart, end);
}

export type ReadingFrameAAMap = {[k in ReadingFrameName]: AminoAcidMap};

/**
 *  Get a map of amino acid index starts to aa letters.
 *
 *  {
 *    FWD1: {
 *      0: L,
 *      3: R,
 *      6: M
 *    },
 *    FWD2: {...},
 *    FWD3: {...},
 *  }
 */
export function getReadingFrameAAMap(
  gapSequenceWrapper: GapSequenceWrapper,
  trimStart: number,
  trimEnd: number
): ReadingFrameAAMap {
  const masterFrameStarts = getFrameStarts(gapSequenceWrapper.bases(), trimStart);
  const frameAcids = READING_FRAME_NAMES.map((frameName: ReadingFrameName) => {
    const start = masterFrameStarts[frameName];
    return start != null ? getReadingFrameAminoAcids(gapSequenceWrapper, start, start, trimEnd) : [];
  });
  const frameMaps = frameAcids.map((acids) =>
    acids.reduce((map: AminoAcidMap, acid: AminoAcid) => {
      map[acid.indexStart] = acid.b;
      return map;
    }, {})
  );
  return _.object(READING_FRAME_NAMES, frameMaps);
}

function visibilityToggledOn(visibilitySetting: Model): boolean {
  return Boolean(visibilitySetting.get('value'));
}

function validMasterFrameStart(masterFrameStarts: FrameStarts, visibilitySetting: Model): boolean {
  const frameName = visibilitySetting.get('name') as ReadingFrameName;
  const frameStart = masterFrameStarts[frameName];
  // eslint-disable-next-line benchlint/no-eqeqeq-null
  return frameStart !== null;
}

/**
 * Return the index a reading frame should start on.
 *
 * May be undefined if no valid reading frame exists. Meant to answer the question
 *  "Given amino acids on a master sequence and the bases of
 *  a non-master sequence, what index should we start the non-master reading frame on?"
 *
 * The reading frame should start on the first master codon that starts after the trim and is not a gap.
 *
 * In the example below, assuming trimStart of 0, the return value is 3 because that's the start of GGG, the
 * first codon on the master where there's a non-gap at the same index on the non-master.
 *             012345
 * master:     AAAGGG
 * non-master: -AAGGG
 */
export function readingFrameStartIndex(
  masterAminoAcids: Array<AminoAcid>,
  trimStart: number,
  bases: string
): number | null {
  const firstNonGapAminoAcid = masterAminoAcids.find(
    (aa) => aa.indexStart >= trimStart && bases[aa.indexStart] !== GAP
  );
  return firstNonGapAminoAcid ? firstNonGapAminoAcid.indexStart : null;
}

export type AminoAcidsByFrameName = {[k in ReadingFrameName]: Array<AminoAcid>};

function validAminoAcidsinTranslation(
  aminoAcidsByFrameName: AminoAcidsByFrameName,
  visibilitySetting: Model,
  trimStart: number,
  bases: string
): boolean {
  const frameName = visibilitySetting.get('name') as ReadingFrameName;
  const masterAminoAcids = aminoAcidsByFrameName[frameName];

  // Find the first master amino acid after the sequence's frame starts that isn't on a gap
  // eslint-disable-next-line benchlint/no-eqeqeq-null
  return readingFrameStartIndex(masterAminoAcids, trimStart, bases) !== null;
}

/**
 * Determine if frame is visible
 *
 * Frame visibility depends on 3 things:
 * 1. Does the user want to see the frame? (determined by the visibilitySetting)
 * 2. Is there a valid master frame to compare to? In example below, the second and third reading
 *    frames don't have a valid master frame because there are no more bases after the first
 *    master:     A--
 *    non-master: AAA
 * 3. Are there valid amino acids to render in the non-master sequence? In the example below,
 *    all three master frames are valid, but the second and third reading frames don't have
 *    any complete codons
 *    master:     AAAGGG
 *    non-master: AAA---
 */
export function frameIsVisible(
  masterFrameStarts: FrameStarts,
  aminoAcidsByFrameName: AminoAcidsByFrameName,
  visibilitySetting: Model,
  trimStart: number,
  bases: string
): boolean {
  return (
    visibilityToggledOn(visibilitySetting) &&
    validMasterFrameStart(masterFrameStarts, visibilitySetting) &&
    validAminoAcidsinTranslation(aminoAcidsByFrameName, visibilitySetting, trimStart, bases)
  );
}
